import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { buildVendorParam } from "../utils/vendorSlug";
import CrawlLink from "../components/CrawlLink";
import VendorDirectoryLinks from "../components/VendorDirectoryLinks";
import {
  coverageEntryFor,
  coverageKeyIdFor,
  flattenVendorLocations,
  locationCountLabel,
  locationReaches,
  onePerVendor,
  withDistance,
  withOutletQuery,
} from "../utils/vendorOutlets";
import { openNowOf, vendorOpenBadge } from "../utils/vendorOpenState";
import DeliveryBadge from "../components/DeliveryBadge";
import useCoverageBatch from "../redux/useCoverageBatch";
import useDeliveryPoint from "../hooks/useDeliveryPoint";
import { calculateDistance } from "../redux/userLocationSlice";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { fetchBrands } from "../redux/brandSlice";
import useCatalogUpdates from "../hooks/useCatalogUpdates";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ArticleIcon from "@mui/icons-material/Article";
import FeedIcon from "@mui/icons-material/Feed";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CircularProgress from "@mui/material/CircularProgress";
import vendorIcon from "../assets/icon.png";
import DOMPurify from "dompurify";
import { SEOHead } from "../components/SEO/SEOHead";
import { COUNTRY_NAME } from "../config/market";
import OfficialStoreBadge from "../components/OfficialStoreBadge";
import { getBrandThemeCssVars } from "../utils/brandTheme";
import VendorTagline from "../components/VendorTagline";

const AllBrandsPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { brands, isFetching } = useSelector((state) => state.brands);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    dispatch(fetchBrands());
  }, [dispatch]);

  // Live: a brand or one of its outlets switched on or off on the dashboard.
  // Every active outlet is a card on this page, so either changes WHICH cards
  // may show. This page had no socket at all, so a branch switched off stayed
  // listed until the visitor reloaded (4 Oct 2026). Refetch rather than splice:
  // the server owns what a customer may see.
  useCatalogUpdates({
    outletStatusChanged: (payload) => {
      if (payload.parentType === "brand" && payload.brandId) dispatch(fetchBrands());
    },
    "brand:statusChanged": (payload) => {
      if (payload.brandId) dispatch(fetchBrands());
    },
  });

  // Brands and their outlets as one flat list of locations, every outlet
  // right after its brand (utils/vendorOutlets, the same flattening the
  // restaurant and pharmacy listings use), grouped below into one card per
  // brand. An outlet row wears the BRAND's chrome (logo, theme, badge,
  // catalogue) and the OUTLET's identity (name, address, hours, coverage),
  // so the row keeps the parent beside it.
  const allLocations = useMemo(
    () => flattenVendorLocations(brands, { outletRow: (brand) => ({ brand }) }),
    [brands],
  );
  // Each location's straight-line distance from the shopper's browse point,
  // for choosing which branch a card opens on and for the "1.2 km" on it.
  // Unchanged when no location is set.
  const deliveryPoint = useDeliveryPoint();
  const locatedLocations = useMemo(
    () =>
      allLocations.map((location) =>
        withDistance(location, deliveryPoint, calculateDistance),
      ),
    [allLocations, deliveryPoint],
  );
  const brandOf = (location) => (location.isOutlet ? location.brand : location);
  // The id orders, quotes and coverage are made against: the brand's own id,
  // for the brand card and for each of its outlets alike (the outlet rides
  // along as `outletId`).
  const brandIdFor = (location) => String(brandOf(location)._id);

  // Authoritative coverage for every card in one batched call, measured from
  // the outlet's own door for an outlet card: the same verdict the brand
  // storefront's banner and the order-create gate give.
  const coverageEntries = useMemo(
    () => allLocations.map((location) => coverageEntryFor(location, brandIdFor(location))),
    // brandIdFor is a stable pure function over the row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allLocations],
  );
  const { byVendorId: coverageByVendor } = useCoverageBatch({
    entries: coverageEntries,
    enabled: coverageEntries.length > 0,
    vendorType: "brand",
  });

  /**
   * The one place that knows what a brand storefront URL looks like. An outlet
   * opens the BRAND's storefront with `?outletId=`, exactly as a restaurant
   * outlet does; the storefront then checks hours, coverage and the fee
   * against that outlet and stamps it on every cart line.
   */
  const buildBrandPath = (location) => {
    const brand = brandOf(location);
    const param = buildVendorParam(brand.tradingName || brand.name, brand._id);
    return withOutletQuery(
      `/brand/shop/${param}`,
      location.isOutlet ? location._id : null,
    );
  };

  const handleBrandClick = (location) => {
    history.push(buildBrandPath(location));
  };

  // Filter locations on the search query: a brand by its names and address,
  // an outlet by its own name and address and by the brand it belongs to.
  const query = searchQuery.trim().toLowerCase();
  const filteredLocations = query
    ? locatedLocations.filter((location) => {
        const brand = brandOf(location);
        return [location.name, location.address, brand.name, brand.tradingName]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      })
    : locatedLocations;

  // ONE CARD PER BRAND (4 Oct 2026). A chain used to fill the page: Yalelo's
  // 19 shops were 20 near-identical cards. Each brand now shows once, opened
  // on the location that serves this shopper best (open, and the backend
  // coverage says it delivers here, nearest first; see
  // utils/vendorOutlets.onePerVendor), with the number of locations on the
  // card. The storefront's location picker lists them all. A search for
  // "Bulaga" still finds Yalelo, opened on that shop, and the card still says
  // how many locations Yalelo has.
  const shownBrands = onePerVendor(filteredLocations, {
    allCards: locatedLocations,
    isOpen: openNowOf,
    reaches: (location) => {
      const verdict =
        coverageByVendor[coverageKeyIdFor(location, brandIdFor(location))];
      return verdict?.status === "succeeded" &&
        typeof verdict.deliverable === "boolean"
        ? verdict.deliverable
        : locationReaches(location);
    },
  });

  return (
    <>
      <SEOHead
        title="Brand partners & suppliers"
        description={`Discover verified brand storefronts on Lusuku — retail and B2B-friendly suppliers with delivery across ${COUNTRY_NAME}. Sign in to browse each brand catalogue and prices.`}
        canonicalPath="/brands"
        keywords={[
          "Lusuku",
          COUNTRY_NAME,
          "brands",
          "B2B",
          "wholesale",
          "suppliers",
          "brand shop",
        ]}
      />
      <Navbar />
      <PageContainer>
        <HeaderSection>
          <BackgroundIcons>
            <IconWrapper
              style={{
                top: "10%",
                left: "5%",
                transform: "rotate(-15deg)",
                "--delay": "0s",
              }}
            >
              <NewspaperIcon />
            </IconWrapper>
            <IconWrapper
              style={{
                top: "20%",
                right: "8%",
                transform: "rotate(20deg)",
                "--delay": "0.5s",
              }}
            >
              <MenuBookIcon />
            </IconWrapper>
            <IconWrapper
              style={{
                top: "50%",
                left: "3%",
                transform: "rotate(-25deg)",
                "--delay": "1s",
              }}
            >
              <ArticleIcon />
            </IconWrapper>
            <IconWrapper
              style={{
                top: "60%",
                right: "5%",
                transform: "rotate(15deg)",
                "--delay": "1.5s",
              }}
            >
              <FeedIcon />
            </IconWrapper>
            <IconWrapper
              style={{
                top: "30%",
                left: "12%",
                transform: "rotate(10deg)",
                "--delay": "2s",
              }}
            >
              <ImportContactsIcon />
            </IconWrapper>
            <IconWrapper
              style={{
                top: "70%",
                right: "12%",
                transform: "rotate(-20deg)",
                "--delay": "2.5s",
              }}
            >
              <NewspaperIcon />
            </IconWrapper>
            <IconWrapper
              style={{
                top: "15%",
                left: "50%",
                transform: "rotate(-10deg)",
                "--delay": "3s",
              }}
            >
              <ArticleIcon />
            </IconWrapper>
            <IconWrapper
              style={{
                top: "75%",
                left: "20%",
                transform: "rotate(25deg)",
                "--delay": "0.3s",
              }}
            >
              <MenuBookIcon />
            </IconWrapper>
            <IconWrapper
              style={{
                top: "40%",
                right: "15%",
                transform: "rotate(-15deg)",
                "--delay": "0.8s",
              }}
            >
              <FeedIcon />
            </IconWrapper>
            <IconWrapper
              style={{
                top: "85%",
                left: "8%",
                transform: "rotate(12deg)",
                "--delay": "1.3s",
              }}
            >
              <ImportContactsIcon />
            </IconWrapper>
            <IconWrapper
              style={{
                top: "25%",
                right: "25%",
                transform: "rotate(-18deg)",
                "--delay": "1.8s",
              }}
            >
              <NewspaperIcon />
            </IconWrapper>
            <IconWrapper
              style={{
                top: "55%",
                left: "25%",
                transform: "rotate(22deg)",
                "--delay": "2.3s",
              }}
            >
              <ArticleIcon />
            </IconWrapper>
          </BackgroundIcons>
          <HeaderContent>
            <Title>Shop Brands</Title>
            <Subtitle>
              Discover products from your favorite brands
            </Subtitle>
            <SearchBar>
              <SearchInput
                type="text"
                placeholder="Search brands by name or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchBar>
          </HeaderContent>
        </HeaderSection>

        <ContentSection>
          {isFetching ? (
            <LoadingContainer>
              <CircularProgress style={{ color: "#1976d2" }} size={50} />
              <LoadingText>Loading brands...</LoadingText>
            </LoadingContainer>
          ) : shownBrands.length === 0 ? (
            <EmptyState>
              <EmptyStateIconWrapper>
                <StorefrontIcon style={{ fontSize: 80, color: '#1976d2' }} />
              </EmptyStateIconWrapper>
              <EmptyStateTitle>No Brands Available</EmptyStateTitle>
              <EmptyStateText>
                {searchQuery
                  ? "Try adjusting your search criteria"
                  : "No brands available at the moment"}
              </EmptyStateText>
            </EmptyState>
          ) : (
            <>
              <ResultsCount>
                {/* Brands, not locations: a chain is one card. */}
                Showing {shownBrands.length} brand
                {shownBrands.length !== 1 ? "s" : ""}
              </ResultsCount>
              <BrandsGrid>
                {shownBrands.map((location) => {
                  const brand = brandOf(location);
                  // The server-computed open state for the location this
                  // card opens on: the outlet's own hours (inheriting the
                  // brand's when it has none), or the brand's own. Unknown
                  // draws nothing.
                  const openBadge = vendorOpenBadge(location);
                  // "20 locations" on a chain's card; null for a brand with
                  // one place to order from.
                  const chainLabel = locationCountLabel(location.locationCount);
                  return (
                    <BrandCard
                      key={`brand-${location.vendorKey}`}
                      onClick={() => handleBrandClick(location)}
                      style={getBrandThemeCssVars(brand)}
                    >
                      <CardGlow />
                      <CrawlLink
                        to={buildBrandPath(location)}
                        onNavigate={() => handleBrandClick(location)}
                        style={{ display: "block" }}
                      >
                        <BrandImageWrapper>
                          <BrandImage
                            src={brand.imageUrl || brand.bannerUrl || vendorIcon}
                            alt={brand.name}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = vendorIcon;
                            }}
                          />
                          {openBadge && (
                            <StatePill $tone={openBadge.tone}>{openBadge.label}</StatePill>
                          )}
                        </BrandImageWrapper>
                      </CrawlLink>
                      <BrandName>
                        <CrawlLink
                          to={buildBrandPath(location)}
                          onNavigate={() => handleBrandClick(location)}
                        >
                          {brand.name}
                        </CrawlLink>
                      </BrandName>
                      <CardTagline vendor={brand} />
                      {chainLabel ? (
                        <>
                          {/* Which of the brand's locations this card opens
                              on, and how far it is. Changed from the
                              storefront's location picker. */}
                          <ChainLocation>
                            {location.isOutlet ? location.name : "Main branch"}
                            {location.distance ? ` · ${location.distance} km` : ""}
                          </ChainLocation>
                          <ChainCount>{chainLabel}</ChainCount>
                        </>
                      ) : (
                        brand.tradingName &&
                        brand.tradingName !== brand.name && (
                          <TradingName>{brand.tradingName}</TradingName>
                        )
                      )}
                      {brand.description && (
                        <Description
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(brand.description, { ALLOWED_TAGS: [] }),
                          }}
                        />
                      )}
                      {brand.verifiedVendorBadge !== false && (
                        <BadgeWrapper>
                          <OfficialStoreBadge />
                        </BadgeWrapper>
                      )}
                      <Address>
                        <LocationOnIcon style={{ fontSize: 16 }} />
                        <span>{location.address || "Multiple locations"}</span>
                      </Address>
                      {(brand.fulfilment?.deliveryCoverage === "NATIONAL" ||
                        location.deliveryZoneRadius) && (
                        <DeliveryRadius>
                          <LocationOnIcon
                            style={{ fontSize: 14 }}
                          />
                          <span>
                            {brand.fulfilment?.deliveryCoverage === "NATIONAL"
                              ? "Nationwide delivery"
                              : `Delivery radius: ${location.deliveryZoneRadius}km`}
                          </span>
                        </DeliveryRadius>
                      )}
                      <CardFooter>
                        <DeliveryBadge
                          vendor={location}
                          coverage={
                            coverageByVendor[
                              coverageKeyIdFor(location, brandIdFor(location))
                            ]
                          }
                          size="small"
                        />
                      </CardFooter>
                      <OrderButton>Shop Now</OrderButton>
                    </BrandCard>
                  );
                })}
              </BrandsGrid>
            </>
          )}
        </ContentSection>
        {/* Every brand storefront, including the ones the cards above leave out. */}
        <VendorDirectoryLinks vertical="brand" title="All brands on Lusuku" />
      </PageContainer>
      <Footer />
    </>
  );
};

export default AllBrandsPage;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding-top: var(--lusuku-nav-h, 130px);

  @media (max-width: 850px) {
    padding-top: var(--lusuku-nav-h, 130px);
  }
`;

const HeaderSection = styled.div`
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%);
  padding: 3rem 1rem;
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
`;

const BackgroundIcons = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
`;

const IconWrapper = styled.div`
  position: absolute;
  opacity: 0.08;
  color: #0d47a1;
  transition: opacity 0.3s ease;

  svg {
    font-size: 3rem;

    @media (max-width: 768px) {
      font-size: 2rem;
    }

    @media (max-width: 480px) {
      font-size: 1.5rem;
    }
  }

  &:hover {
    opacity: 0.12;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  animation: float 6s ease-in-out infinite;
  animation-delay: var(--delay, 0s);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const Title = styled.h1`
  color: #0d47a1;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-family: "Poppins", sans-serif;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  color: #1565c0;
  font-size: 1.1rem;
  margin-bottom: 2rem;

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const SearchBar = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 20px;
  border: 2px solid rgba(25, 118, 210, 0.3);
  border-radius: 12px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  font-family: "Inter", sans-serif;

  &:focus {
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const ContentSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem 3rem 1rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
  gap: 1rem;
`;

const LoadingText = styled.p`
  color: #666;
  font-size: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 1rem;
`;

const EmptyStateIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 50%;
  box-shadow: 0 4px 20px rgba(25, 118, 210, 0.2);
`;

const EmptyStateTitle = styled.h2`
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const EmptyStateText = styled.p`
  color: #666;
  font-size: 1rem;
`;

const ResultsCount = styled.p`
  color: #666;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
`;

const BrandsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    gap: 1.5rem;
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const BrandCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 1.125rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  }
`;

const CardGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--brand-secondary, #c9a227);
  opacity: 0;
  transition: opacity 0.3s ease;

  ${BrandCard}:hover & {
    opacity: 1;
  }
`;

const BrandImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  margin-bottom: 0.75rem;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 3px solid #fff;

  @media (max-width: 960px) {
    height: 220px;
  }

  @media (max-width: 620px) {
    height: clamp(190px, 58vw, 260px);
  }
`;

const BrandImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 0.5rem;
  box-sizing: border-box;
  transition: filter 0.3s ease;

  ${BrandCard}:hover & {
    filter: brightness(0.98);
  }
`;

/* The server-computed open state, top-left on the image like the restaurant
   cards. Tones follow vendorOpenBadge: open, scheduled (order ahead), closed,
   unavailable. */
const StatePill = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  font-size: 0.72rem;
  font-weight: 700;
  color: ${(props) =>
    props.$tone === "open"
      ? "#17815e"
      : props.$tone === "scheduled"
        ? "#1d4ed8"
        : "#b23b3b"};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.18);

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  margin: 0.25rem 0 0.75rem;
`;

const Description = styled.p`
  color: #666;
  font-size: 0.875rem;
  margin: 0.5rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/* The vendor's own tagline under its name. Nothing renders when the vendor
   has not written one (components/VendorTagline.jsx). */
const CardTagline = styled(VendorTagline)`
  margin: 2px 0 8px;
`;

const BrandName = styled.h3`
  color: var(--brand-primary, #324d54);
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  font-family: "Poppins", sans-serif;
`;

const TradingName = styled.p`
  color: #666;
  font-size: 0.85rem;
  font-style: italic;
  margin-bottom: 0.5rem;
`;

/* A chain's card: the location it opens on, then how many it has. */
const ChainLocation = styled.p`
  color: #334155;
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0 0 0.15rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChainCount = styled.p`
  color: var(--brand-primary, #324d54);
  font-size: 0.8rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
`;

const BadgeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const Address = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;

  svg {
    color: var(--brand-secondary, #c9a227);
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DeliveryRadius = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--brand-primary, #324d54);
  font-size: 0.85rem;
  font-weight: 500;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e0e0e0;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const OrderButton = styled.button`
  margin-top: auto;
  padding: 10px 20px;
  background: var(--brand-secondary, #c9a227);
  color: var(--brand-on-secondary, #0f172a);
  border: none;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.2);
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: var(--brand-secondary-hover, #ad8b22);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.28);
  }

  &:active {
    transform: translateY(0);
  }
`;
